import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { CustomSwitch } from './StyledComponents';
import React from 'react';
import { Add, Remove } from '@mui/icons-material';
import { MODE_CONVERSION_OPTIONS, type ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import {
  FINGERING_DISPLAY_LABELS,
  FINGERING_DISPLAY_LEVELS,
  type FingeringDisplay,
} from 'Fingering/display';

const SETTINGS_PANEL_Z_INDEX = 3000;
const SETTINGS_SELECT_MENU_Z_INDEX = SETTINGS_PANEL_Z_INDEX + 2000;
const MAX_TRANSPOSE_SEMITONES = 7;
const TRANSPOSE_SEMITONE_OPTIONS = Array.from(
  { length: MAX_TRANSPOSE_SEMITONES * 2 + 1 },
  (_, index) => index - MAX_TRANSPOSE_SEMITONES,
);
const getTransposeLabel = (semitoneSteps: number): string => {
  if (semitoneSteps === 0) {
    return 'Concert (0)';
  }
  const absSemitones = Math.abs(semitoneSteps);
  return `${semitoneSteps > 0 ? '+' : ''}${semitoneSteps} ${
    absSemitones === 1 ? 'semitone' : 'semitones'
  }`;
};

const selectSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '42px',
    color: '#ECECED',
    backgroundColor: '#243056',
    borderRadius: '8px',
    fontFamily: 'Lato',
    fontSize: '14px',
    fontWeight: 600,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 36px 8px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#525969',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiSvgIcon-root': {
    color: '#ECECED',
  },
} as const;

const selectMenuProps = {
  sx: {
    zIndex: SETTINGS_SELECT_MENU_Z_INDEX,
  },
  PaperProps: {
    sx: {
      zIndex: SETTINGS_SELECT_MENU_Z_INDEX + 1,
      mt: '6px',
      color: '#ECECED',
      backgroundColor: '#10192E',
      border: '1px solid #3E4C73',
      borderRadius: '10px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
      maxHeight: '260px',
      '& .MuiMenuItem-root': {
        minHeight: '36px',
        fontFamily: 'Lato',
        fontSize: '14px',
        fontWeight: 600,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(111, 135, 241, 0.32)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.22)',
      },
    },
  },
} as const;

const CustomSlider = ({
  value,
  onChange,
  min,
  max,
  step,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
}) => {
  return (
    <input
      className="custom-slider"
      type="range"
      value={value}
      onChange={(e) => onChange(e.target.valueAsNumber)}
      min={min}
      max={max}
      step={step}
    />
  );
};

const ZoomSlider = ({
  zoomLevel,
  setZoomLevel,
}: {
  zoomLevel: number;
  setZoomLevel: (zoomLevel: number) => void;
}) => {
  const [minZoomLevel, maxZoomLevel] = [0.5, 1];

  return (
    <Box
      className="zoom-slider-container open"
      sx={{
        //position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        top: 10,
        right: 10,
        //width: '200px',
        width: '100%',
        height: '34px',
        padding: '5px',
        zIndex: 1,
        cursor: 'pointer',
      }}
    >
      <Typography>Zoom Level</Typography>
      <Box
        /*className={isOpen ? "inner-container vis" : "inner-container hid"}*/
        sx={{
          flexGrow: 1,
          transition: '0.5s ease-in-out',
          //marginRight: '35px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Remove
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.max(minZoomLevel, zoomLevel - 0.05))}
        />
        <CustomSlider
          min={minZoomLevel}
          step={0.05}
          max={maxZoomLevel}
          value={Math.max(minZoomLevel, Math.min(maxZoomLevel, zoomLevel))}
          onChange={(value) => setZoomLevel(value)}
        />
        <Add
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.min(maxZoomLevel, zoomLevel + 0.05))}
        />
      </Box>
      {process.env.REACT_APP_NODE_ENV != 'prd' && (
        <p
          style={{
            color: '#000',
            opacity: 0.5,
            position: 'absolute',
            right: 10,
            top: 30,
            fontSize: 12,
          }}
        >
          [debug] Zoom Level: {(zoomLevel ?? 0.9).toFixed(2)}
        </p>
      )}
    </Box>
  );
};

type SettingsPanelProps = {
  open: boolean;
  onClose?: () => void;
  setZoomLevel: (zoomLevel: number) => void;
  setCursorVisible: (visible: boolean) => void;
  setPianoSound: (sound: boolean) => void;
  setMetronomeSound: (sound: boolean) => void;
  pianoSound: boolean;
  cursorVisible: boolean;
  zoomLevel: number;
  metronomeSound: boolean;
  setShowDebugTierSummary: (x: boolean) => void;
  showDebugTierSummary: boolean;
  setShowDebugRepertoireUnlocked: (x: boolean) => void;
  showDebugRepertoireUnlocked: boolean;
  showTransposeControls?: boolean;
  transposeSemitones?: number;
  onTransposeChange?: (nextSemitones: number) => void;
  modeConversion?: ParallelModeConversion;
  onModeConversionChange?: (nextMode: ParallelModeConversion) => void;
  currentKeyLabel?: string | null;
  showAudioCalibrationAction?: boolean;
  onOpenAudioCalibration?: () => void;
  /** Finger-number density; the control is hidden while this is undefined. */
  fingeringDisplay?: FingeringDisplay;
  onFingeringDisplayChange?: (nextDisplay: FingeringDisplay) => void;
};

export default function SettingsPanel({
  onClose,
  zoomLevel,
  setZoomLevel,
  pianoSound,
  setPianoSound,
  metronomeSound,
  setMetronomeSound,
  cursorVisible,
  setCursorVisible,
  setShowDebugTierSummary,
  showDebugTierSummary,
  setShowDebugRepertoireUnlocked,
  showDebugRepertoireUnlocked,
  showTransposeControls = false,
  transposeSemitones = 0,
  onTransposeChange,
  modeConversion = 'off',
  onModeConversionChange,
  currentKeyLabel,
  showAudioCalibrationAction = false,
  onOpenAudioCalibration,
  fingeringDisplay,
  onFingeringDisplayChange,
}: SettingsPanelProps) {
  const rowStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    color: '#ECECED',
  };
  return (
    <>
      {onClose && (
        <Box
          onClick={onClose}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: SETTINGS_PANEL_Z_INDEX - 1,
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          zIndex: SETTINGS_PANEL_Z_INDEX,
          left: '10px',
          bottom: '60px',
          backgroundColor: '#050B13',
          padding: '16px 32px',
          borderRadius: '8px',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10px', // Half of the carot size to move it above the modal
            left: '10px', // Align this with the hamburger icon
            width: '0',
            height: '0',
            borderLeft: '16px solid transparent',
            borderRight: '16px solid transparent',
            borderTop: '16px solid #050B13', // Carot color should match modal background
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            textTransform: 'uppercase',
            marginBottom: '16px',
            paddingBottom: '10px',
            borderBottom: '1px solid #414753',
          }}
        >
          Settings
        </Typography>
        <Box>
          <Box sx={{ ...rowStyles, flexDirection: 'column', marginBottom: '10px' }}>
            <ZoomSlider zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
          </Box>
        </Box>
        <Box>
          <Box sx={rowStyles}>
            <CustomSwitch
              checked={pianoSound}
              onChange={(_, value) => setPianoSound(value as boolean)}
            />
            <Typography variant="body1">Keyboard Sfx</Typography>
          </Box>
        </Box>
        <Box sx={rowStyles}>
          <CustomSwitch
            checked={metronomeSound}
            onChange={(_, value) => setMetronomeSound(value as boolean)}
          />
          <Typography variant="body1">Metronome Sound</Typography>
        </Box>
        {showAudioCalibrationAction && onOpenAudioCalibration && (
          <Box sx={{ marginTop: '12px', marginBottom: '6px' }}>
            <Button
              data-lesson-audio-calibration
              fullWidth
              variant="outlined"
              onClick={onOpenAudioCalibration}
              sx={{
                minHeight: '44px',
                borderColor: '#6F87F1',
                borderRadius: '9px',
                color: '#F2F6FF',
                fontFamily: 'Lato',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#8EA1FF',
                  backgroundColor: 'rgba(111, 135, 241, 0.12)',
                },
              }}
            >
              Recalibrate acoustic piano
            </Button>
          </Box>
        )}
        {showTransposeControls && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
            <Box>
              <Typography variant="body1" sx={{ marginBottom: '6px' }}>
                Transpose
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={`${transposeSemitones}`}
                onChange={(event) => onTransposeChange?.(Number(event.target.value))}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={selectSx}
              >
                {TRANSPOSE_SEMITONE_OPTIONS.map((step) => (
                  <MenuItem key={step} value={`${step}`}>
                    {getTransposeLabel(step)}
                  </MenuItem>
                ))}
              </TextField>
              {currentKeyLabel && (
                <Typography variant="body2" sx={{ marginTop: '6px', color: '#C9D2F5' }}>
                  Now in {currentKeyLabel}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="body1" sx={{ marginBottom: '6px' }}>
                Scale / Mode
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={modeConversion}
                onChange={(event) =>
                  onModeConversionChange?.(event.target.value as ParallelModeConversion)
                }
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={selectSx}
              >
                {MODE_CONVERSION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        )}
        {fingeringDisplay !== undefined && (
          <Box sx={{ marginTop: '10px', marginBottom: '10px' }}>
            <Typography variant="body1" sx={{ marginBottom: '6px' }}>
              Finger numbers
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={fingeringDisplay}
              onChange={(event) =>
                onFingeringDisplayChange?.(event.target.value as FingeringDisplay)
              }
              SelectProps={{ MenuProps: selectMenuProps }}
              inputProps={{ 'aria-label': 'Finger numbers' }}
              sx={selectSx}
            >
              {FINGERING_DISPLAY_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {FINGERING_DISPLAY_LABELS[level]}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <Box sx={rowStyles}>
          <CustomSwitch
            checked={cursorVisible}
            onChange={(_, value) => setCursorVisible(value as boolean)}
          />
          <Typography variant="body1">Cursor</Typography>
        </Box>
        {process.env.REACT_APP_NODE_ENV !== 'prd' && (
          <Box sx={rowStyles}>
            <CustomSwitch
              checked={showDebugTierSummary}
              onChange={(_, value) => setShowDebugTierSummary(value as boolean)}
            />
            <Typography variant="body1">Debug Tier Summary</Typography>
          </Box>
        )}
        {process.env.REACT_APP_NODE_ENV !== 'prd' && (
          <Box sx={rowStyles}>
            <CustomSwitch
              checked={showDebugRepertoireUnlocked}
              onChange={(_, value) => setShowDebugRepertoireUnlocked(value as boolean)}
            />
            <Typography variant="body1">Debug Repertoire Unlocked</Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
